import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../firebase';
import { doc, getDoc, setDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  Box, Typography, Paper, Button, TextField, Alert, CircularProgress,
  Grid, Tooltip, MenuItem, FormControlLabel, Switch, Tabs, Tab, Chip
} from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import UploadIcon from '@mui/icons-material/Upload';
import SaveIcon from '@mui/icons-material/Save';
import Layout from '../components/Layout';
import { logError } from '../utils/errorLogger';

const DEFAULT_BRANDING = {
  clubName:        'Torch Beach Volleyball',
  tagline:         '',
  facilityName:    'Corona Beach',
  facilityAddress: '2020 South Dean Road, Orlando, FL 32825',
  primaryColor:    '#1E3A5F',
  secondaryColor:  '#2E6DA4',
  accentColor:     '#C9A84C',
  // Optional advanced theme fields. Leave blank to use derived defaults from the
  // primary/secondary/accent colors above.
  pageBg:          '',  // page content background (default: #f0f4f8)
  pageBgDark:      '',  // dark page bg for marketing/account pages (default: #0D1E35)
  logoUrl:         '',
  faviconUrl:      '',
  timezone:        'America/New_York',
  adminNotificationEmail: '',  // recipient for new-registration notices (blank = admin@torchvolleyball.com)
  // Text alerts for the same events the notification email covers. Kept beside
  // it rather than in a doc of its own, so "who hears about a registration" is
  // one screen. Blank or switched off means nothing is sent.
  adminNotificationSms: '',    // comma-separated; any US format, stored as typed
  smsOnRegistration: false,
  smsOnCancellation: false,    // a switch of its own: rare, and wanted
  smsOnStorePurchase: false,   // an order is a job for someone, not just news
  smsOnGateOffline: false,
  smsOnBookingApproval: false,  // a Gold member asking for high-school hours
  // Group chat invites sent in the welcome email. Juniors and adults use
  // different platforms, so each carries its own label and link.
  juniorGroupLabel: 'GroupMe',
  juniorGroupLink:  '',
  adultGroupLabel:  'WhatsApp',
  adultGroupLink:   '',
};

// What each role is for, shown on the Roles tab. Written from what the code
// does, not what the name suggests — keep it in step when a role's reach
// changes. Roles themselves are stored as plain strings on users.roles, and
// the lists that decide who can be GIVEN one (Edit Profile, Manage Roles,
// MENU_ROLES) are separate and drift; `assign` says where each really comes from.
const ROLE_GUIDE = [
  { group: 'Staff', roles: [
    { key: 'admin', label: 'Admin',
      does: 'Full access: Administration, editing people and roles, memberships and billing, reports, gate and every setting. Can drag classes and reservations on the calendar, and move member bookings between courts.',
      assign: 'Edit Profile or Manage Roles' },
    { key: 'coach', label: 'Coach',
      does: 'Opens on the Coach Dashboard: their classes, hours and pay. Sees class rosters and checks athletes in, and is notified about changes to their own classes. On People, sees the Juniors and Parents tabs. Cannot move anything on the calendar.',
      assign: 'Edit Profile or Manage Roles' },
    { key: 'assistant_coach', label: 'Assistant Coach',
      does: 'The same access as Coach, paid at the assistant rate.',
      assign: 'Edit Profile or Manage Roles' },
    { key: 'tournament_admin', label: 'Tournament Admin',
      does: 'Runs tournaments and leagues from the Tournament Admin area, and can create, edit and delete calendar reservations. No other admin rights.',
      assign: 'Edit Profile' },
    { key: 'court_admin', label: 'Calendar Manager',
      does: 'On Schedule, sees which member holds each court booking and can move a booking to another court. The date and times never change, so hours and cost stay the same. Cannot cancel bookings and has no admin rights. Counts as staff: skips member registration and appears on Admin Users.',
      assign: 'Edit Profile or Manage Roles' },
    { key: 'member_manager', label: 'Member Manager',
      does: 'Read-only access to the Members tab on People, including session balances and membership details. Cannot edit, archive or delete anyone. Counts as staff: skips member registration and appears on Admin Users.',
      assign: 'Edit Profile or Manage Roles' },
    { key: 'contractor', label: 'Contractor',
      does: 'Logs work hours under Profile → My Work. Each entry emails the Accounts Payable Manager set on the Settings tab.',
      assign: 'Manage Roles' },
    { key: 'volunteer', label: 'Volunteer',
      does: 'Marks an unpaid helper, listed alongside staff on dashboards and coach listings.',
      assign: 'Edit Profile or Manage Roles' },
  ]},
  { group: 'Families and players', roles: [
    { key: 'parent', label: 'Parent',
      does: 'Manages their junior athletes: registration, session balances, class sign-ups and documents.',
      assign: 'Set at registration; also Edit Profile' },
    { key: 'adult_athlete', label: 'Adult Athlete',
      does: 'An adult in training programs, with their own profile, check-ins and session balance.',
      assign: 'Set at registration' },
    { key: 'junior_athlete', label: 'Junior Athlete',
      does: 'A junior who has a login of their own.',
      assign: 'Set at registration' },
    { key: 'player', label: 'Player',
      does: 'An adult who plays without a membership. Gets the adult documents; shown as Player on People.',
      assign: 'Edit Profile or Manage Roles' },
    { key: 'lead', label: 'Lead',
      does: 'A prospect who has not bought anything yet. Shown as Lead on People; Promote to Active removes it.',
      assign: 'Edit Profile or Manage Roles' },
  ]},
  { group: 'Courts and memberships', roles: [
    { key: 'member', label: 'Member',
      does: 'Added automatically when a membership is paid (day passes excluded). Court booking and gate access follow the membership status, not this role: active or trialing gets full access, frozen gets none.',
      assign: 'Automatic — not assigned by hand' },
    { key: 'renter', label: 'Renter',
      does: 'A court-rental account for a group. Books court time, billed monthly or paid at booking depending on the account, and needs a signed waiver and confirmed liability insurance before booking.',
      assign: 'Renter invite' },
  ]},
];

function ColorSwatch({ color }) {
  return (
    <Box sx={{
      width: 36, height: 36, borderRadius: 1,
      backgroundColor: color,
      border: '1px solid rgba(0,0,0,0.15)',
      flexShrink: 0,
    }} />
  );
}

export default function AdminBranding() {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [apManagerId, setApManagerId] = useState('');
  const [tab, setTab] = useState('branding');
  const logoRef    = useRef();
  const faviconRef = useRef();

  useEffect(() => {
    const fetch = async () => {
      try {
        const [snap, finSnap, staffSnap] = await Promise.all([
          getDoc(doc(db, 'settings', 'branding')),
          getDoc(doc(db, 'settings', 'finance')),
          getDocs(query(collection(db, 'users'), where('roles', 'array-contains-any', ['admin', 'coach', 'assistant_coach', 'contractor']))),
        ]);
        if (finSnap.exists()) setApManagerId(finSnap.data().apManagerId || '');
        setStaffOptions(staffSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(u => u.email && !u.id.startsWith('pending_'))
          .sort((a, b) => (a.firstName || '').localeCompare(b.firstName || '')));
        if (snap.exists()) setBranding({ ...DEFAULT_BRANDING, ...snap.data() });
      } catch (e) {
        logError('AdminBranding', e.message || String(e), e.stack || '');
        setError('Failed to load branding settings.');
      }
      setLoading(false);
    };
    fetch();
  }, []);

  const handleChange = (field) => (e) => {
    setBranding(b => ({ ...b, [field]: e.target.value }));
  };

  const handleUpload = async (file, field, setUploading) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const storageRef = ref(storage, `branding/${field}_${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setBranding(b => ({ ...b, [field]: url }));
      setSuccess(`${field === 'logoUrl' ? 'Logo' : 'Favicon'} uploaded successfully.`);
    } catch (e) {
      logError('AdminBranding', e.message || String(e), e.stack || '');
      setError('Upload failed: ' + e.message);
    }
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await setDoc(doc(db, 'settings', 'branding'), branding, { merge: true });
      const ap = staffOptions.find(u => u.id === apManagerId);
      await setDoc(doc(db, 'settings', 'finance'), {
        apManagerId: apManagerId || null,
        apManagerEmail: ap?.email || null,
        apManagerName: ap ? `${ap.firstName || ''} ${ap.lastName || ''}`.trim() : null,
      }, { merge: true });
      setSuccess('Branding settings saved.');
    } catch (e) {
      logError('AdminBranding', e.message || String(e), e.stack || '');
      setError('Save failed: ' + e.message);
    }
    setSaving(false);
  };

  // Mirrors toE164() in functions/index.js. Duplicated rather than shared
  // because the two run in different bundles — but the client copy only
  // EXPLAINS, it never decides: the server re-parses before sending, so a
  // disagreement shows up as a wrong hint, not as an unsent text.
  const toE164 = (raw) => {
    const v = String(raw || '').trim();
    if (!v) return '';
    if (/^\+[1-9]\d{7,14}$/.test(v)) return v;
    const d = v.replace(/\D/g, '');
    if (d.length === 10) return `+1${d}`;
    if (d.length === 11 && d.startsWith('1')) return `+${d}`;
    return '';
  };
  const smsHelp = (() => {
    const raw = String(branding.adminNotificationSms || '').trim();
    if (!raw) return 'Blank means no texts. Separate several numbers with commas.';
    const parts = raw.split(',').map(x => x.trim()).filter(Boolean);
    const good = parts.map(toE164).filter(Boolean);
    const bad = parts.filter(x => !toE164(x));
    if (bad.length) return `Can't read: ${bad.join(', ')} — use a 10-digit US number.`;
    return `Texts go to ${good.join(', ')}`;
  })();

  if (loading) return (
    <Layout><Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box></Layout>
  );

  return (
    <Layout>
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <PaletteIcon sx={{ color: '#1E3A5F', fontSize: 32 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E3A5F' }}>
          Branding
        </Typography>
      </Box>

      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
      {error   && <Alert severity="error"   onClose={() => setError('')}   sx={{ mb: 2 }}>{error}</Alert>}

      {/* One form across the tabs: switching tabs keeps unsaved edits, and
          Save writes every tab at once. */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
        sx={{ mb: 3, borderBottom: '1px solid rgba(0,0,0,0.12)',
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
          '& .Mui-selected': { color: '#1E3A5F !important' },
          '& .MuiTabs-indicator': { backgroundColor: '#C9A84C' } }}>
        <Tab value="branding" label="Branding" />
        <Tab value="colors" label="Colors" />
        <Tab value="logo" label="Logo" />
        <Tab value="settings" label="Settings" />
        <Tab value="groups" label="Group Chats" />
        <Tab value="roles" label="Roles" />
      </Tabs>

      {/* Club Identity */}
      {tab === 'branding' && (
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E3A5F', mb: 2 }}>
          Club Identity
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
              label="Club Name" fullWidth
              value={branding.clubName}
              onChange={handleChange('clubName')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
              label="Tagline" fullWidth placeholder="e.g. Train Hard. Play Smart."
              value={branding.tagline}
              onChange={handleChange('tagline')}
            />
          </Grid>
          {/* The place, not the club. Members name the courts they come to,
              and the two change independently — a club can move facilities. */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
              label="Facility Name" fullWidth placeholder="e.g. Corona Beach"
              value={branding.facilityName}
              onChange={handleChange('facilityName')}
              helperText="Where members play — shown on directions, memberships and pickup"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
              label="Facility Address" fullWidth
              placeholder="e.g. 2020 South Dean Road, Orlando, FL 32825"
              value={branding.facilityAddress}
              onChange={handleChange('facilityAddress')}
              helperText="Full street address, used for directions and store pickup"
            />
          </Grid>
        </Grid>
      </Paper>
      )}

      {/* Colors */}
      {tab === 'colors' && (
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E3A5F', mb: 2 }}>
          Colors
        </Typography>
        <Grid container spacing={3}>
          {[
            { field: 'primaryColor',   label: 'Primary Color',   hint: 'Sidebar, headers' },
            { field: 'secondaryColor', label: 'Secondary Color', hint: 'Buttons, links' },
            { field: 'accentColor',    label: 'Accent Color',    hint: 'Highlights, active items' },
            { field: 'pageBg',         label: 'Page Background', hint: 'Light bg for data pages (default: #f0f4f8)' },
            { field: 'pageBgDark',     label: 'Dark Page BG',    hint: 'Dark bg for marketing pages (default: #0D1E35)' },
            { field: 'primeTintColor',    label: 'Prime Time Tint',    hint: 'Schedule calendar prime hours (default: #DDD2B5)' },
            { field: 'nonPrimeTintColor', label: 'Non-Prime Tint',     hint: 'Schedule calendar regular hours (default: #CCDBEE)' },
          ].map(({ field, label, hint }) => (
            <Grid key={field} size={{ xs: 12, sm: 4 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{label}</Typography>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>{hint}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ColorSwatch color={branding[field] || '#cccccc'} />
                <TextField
                  size="small"
                  sx={{ width: 130, backgroundColor: '#eeeeee', borderRadius: 1 }}
                  value={branding[field] || ''}
                  onChange={handleChange(field)}
                  placeholder="optional"
                  inputProps={{ maxLength: 7 }}
                />
                <Tooltip title="Pick color">
                  <Box
                    component="input"
                    type="color"
                    value={branding[field] || '#ffffff'}
                    onChange={handleChange(field)}
                    sx={{
                      width: 36, height: 36, border: '1px solid rgba(0,0,0,0.15)',
                      borderRadius: 1, cursor: 'pointer', padding: 0,
                    }}
                  />
                </Tooltip>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#888' }}>
          Note: branding changes apply when users next reload the app. Empty values fall back to defaults.
        </Typography>
      </Paper>
      )}

      {/* Logos */}
      {tab === 'logo' && (
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E3A5F', mb: 2 }}>
          Logos
        </Typography>
        <Grid container spacing={3}>

          {/* Main Logo */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Main Logo</Typography>
            <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
              PNG or SVG recommended. Shown in sidebar and login screen.
            </Typography>
            {branding.logoUrl && (
              <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#1E3A5F', borderRadius: 1, display: 'inline-block' }}>
                <img src={branding.logoUrl} alt="Logo" style={{ height: 60, objectFit: 'contain' }} />
              </Box>
            )}
            <Box>
              <input
                type="file" accept="image/*" style={{ display: 'none' }}
                ref={logoRef}
                onChange={e => handleUpload(e.target.files[0], 'logoUrl', setLogoUploading)}
              />
              <Button
                variant="outlined" startIcon={logoUploading ? <CircularProgress size={16} /> : <UploadIcon />}
                onClick={() => logoRef.current.click()}
                disabled={logoUploading}
                sx={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}
              >
                {logoUploading ? 'Uploading…' : 'Upload Logo'}
              </Button>
            </Box>
          </Grid>

          {/* Favicon */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Favicon</Typography>
            <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
              Square image, 32×32 or 64×64px. Shown in browser tab.
            </Typography>
            {branding.faviconUrl && (
              <Box sx={{ mb: 1.5 }}>
                <img src={branding.faviconUrl} alt="Favicon" style={{ height: 32, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 4 }} />
              </Box>
            )}
            <Box>
              <input
                type="file" accept="image/*" style={{ display: 'none' }}
                ref={faviconRef}
                onChange={e => handleUpload(e.target.files[0], 'faviconUrl', setFaviconUploading)}
              />
              <Button
                variant="outlined" startIcon={faviconUploading ? <CircularProgress size={16} /> : <UploadIcon />}
                onClick={() => faviconRef.current.click()}
                disabled={faviconUploading}
                sx={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}
              >
                {faviconUploading ? 'Uploading…' : 'Upload Favicon'}
              </Button>
            </Box>
          </Grid>

        </Grid>
      </Paper>
      )}

      {tab === 'settings' && (
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E3A5F', mb: 2 }}>
        Club Settings
      </Typography>
      <TextField select fullWidth label="Local Timezone" value={branding.timezone || 'America/New_York'}
        onChange={e => setBranding(b => ({ ...b, timezone: e.target.value }))}
        sx={{ mb: 3, maxWidth: 420 }}
        helperText="Used for date and time handling across the app">
        {[
          ['America/New_York', 'Eastern (New York)'],
          ['America/Chicago', 'Central (Chicago)'],
          ['America/Denver', 'Mountain (Denver)'],
          ['America/Phoenix', 'Arizona (Phoenix)'],
          ['America/Los_Angeles', 'Pacific (Los Angeles)'],
          ['America/Anchorage', 'Alaska (Anchorage)'],
          ['Pacific/Honolulu', 'Hawaii (Honolulu)'],
        ].map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
      </TextField>

      <TextField fullWidth label="Admin Notification Email" type="email"
        value={branding.adminNotificationEmail || ''}
        onChange={e => setBranding(b => ({ ...b, adminNotificationEmail: e.target.value }))}
        sx={{ mb: 3, maxWidth: 420 }}
        helperText="Receives new player registration notices. Leave blank to use admin@torchvolleyball.com" />

      <TextField fullWidth label="Text alerts to"
        value={branding.adminNotificationSms || ''}
        onChange={e => setBranding(b => ({ ...b, adminNotificationSms: e.target.value }))}
        sx={{ mb: 1, maxWidth: 420 }}
        placeholder="(321) 689-1123"
        helperText={smsHelp} />
      <FormControlLabel
        sx={{ display: 'block', mb: 3 }}
        control={
          <Switch
            checked={branding.smsOnRegistration === true}
            onChange={e => setBranding(b => ({ ...b, smsOnRegistration: e.target.checked }))}
          />
        }
        label={
          <Typography variant="body2">
            Text on every new registration
            <Typography component="span" variant="caption" sx={{ color: '#888', display: 'block' }}>
              Accounts and league sign-ups. Off means no texts, whatever is in the box above.
            </Typography>
          </Typography>
        } />
      <FormControlLabel
        sx={{ display: 'block', mb: 3 }}
        control={
          <Switch
            checked={branding.smsOnCancellation === true}
            onChange={e => setBranding(b => ({ ...b, smsOnCancellation: e.target.checked }))}
          />
        }
        label={
          <Typography variant="body2">
            Text when a membership is cancelled
            <Typography component="span" variant="caption" sx={{ color: '#888', display: 'block' }}>
              Name, membership, the date they cancelled and the last day they have access.
              Sent when a member cancels, and when a payment finally fails.
            </Typography>
          </Typography>
        } />
      <FormControlLabel
        sx={{ display: 'block', mb: 3 }}
        control={
          <Switch
            checked={branding.smsOnBookingApproval === true}
            onChange={e => setBranding(b => ({ ...b, smsOnBookingApproval: e.target.checked }))}
          />
        }
        label={
          <Typography variant="body2">
            Text when a court request needs approval
            <Typography component="span" variant="caption" sx={{ color: '#888', display: 'block' }}>
              A Gold member asking for a court in high-school hours. Approve or decline under Schools.
            </Typography>
          </Typography>
        } />
      <FormControlLabel
        sx={{ display: 'block', mb: 3 }}
        control={
          <Switch
            checked={branding.smsOnStorePurchase === true}
            onChange={e => setBranding(b => ({ ...b, smsOnStorePurchase: e.target.checked }))}
          />
        }
        label={
          <Typography variant="body2">
            Text when someone buys from the store
            <Typography component="span" variant="caption" sx={{ color: '#888', display: 'block' }}>
              Item, size, quantity, total and buyer. An order needs somebody to set the
              thing aside before the buyer turns up for it, which is what email is worst at.
            </Typography>
          </Typography>
        } />
      <FormControlLabel
        sx={{ display: 'block', mb: 3 }}
        control={
          <Switch
            checked={branding.smsOnGateOffline === true}
            onChange={e => setBranding(b => ({ ...b, smsOnGateOffline: e.target.checked }))}
          />
        }
        label={
          <Typography variant="body2">
            Text when the gate keypad goes offline
            <Typography component="span" variant="caption" sx={{ color: '#888', display: 'block' }}>
              After 30 minutes without a check-in, and again when it is back. Quiet while the
              gate router is off overnight.
            </Typography>
          </Typography>
        } />

      <TextField select fullWidth label="Accounts Payable Manager" value={apManagerId}
        onChange={e => setApManagerId(e.target.value)} sx={{ mb: 3, maxWidth: 420 }}
        helperText="Receives email alerts when contractors submit work entries">
        <MenuItem value="">— None —</MenuItem>
        {staffOptions.map(u => (
          <MenuItem key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.email})</MenuItem>
        ))}
      </TextField>
      </Paper>
      )}

      {tab === 'groups' && (
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E3A5F', mb: 0.5 }}>
        Group Chat Invites
      </Typography>
      <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
        Linked from the welcome email as [[groupLink]], labelled [[groupLabel]].
        Juniors get the first, adults the second. Paste the invite URL from the
        group's own share menu — resetting an invite there breaks the old link,
        so update it here at the same time.
      </Typography>
      {[
        ['Junior', 'juniorGroupLabel', 'juniorGroupLink', 'https://groupme.com/join_group/…'],
        ['Adult',  'adultGroupLabel',  'adultGroupLink',  'https://chat.whatsapp.com/…'],
      ].map(([who, labelField, linkField, placeholder]) => (
        <Box key={who} sx={{ display: 'flex', gap: 2, mb: 2, maxWidth: 720, flexWrap: 'wrap' }}>
          <TextField label={`${who} platform`} value={branding[labelField] || ''}
            onChange={e => setBranding(b => ({ ...b, [labelField]: e.target.value }))}
            sx={{ width: 160 }} helperText="Shown in the email text" />
          <TextField label={`${who} invite link`} value={branding[linkField] || ''}
            onChange={e => setBranding(b => ({ ...b, [linkField]: e.target.value }))}
            sx={{ flexGrow: 1, minWidth: 280 }} placeholder={placeholder}
            error={Boolean(branding[linkField]) && !/^https?:\/\//i.test(branding[linkField])}
            helperText={
              Boolean(branding[linkField]) && !/^https?:\/\//i.test(branding[linkField])
                ? 'Must start with https:// or the link will not work in email'
                : 'Blank keeps the link currently built into the app'
            } />
        </Box>
      ))}
      </Paper>
      )}

      {tab === 'roles' && (
        <Box>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            What each role can do. A person can hold several; access is the sum of them.
            Roles are given from a person's Edit Profile (People → open them → Edit Profile)
            or from Manage Roles on the People list.
          </Typography>
          {ROLE_GUIDE.map(({ group, roles }) => (
            <Paper key={group} elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E3A5F', mb: 2 }}>{group}</Typography>
              {roles.map((r, i) => (
                <Box key={r.key} sx={{ py: 1.5, borderTop: i ? '1px solid rgba(0,0,0,0.08)' : 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F' }}>{r.label}</Typography>
                    <Chip label={r.key} size="small" variant="outlined"
                      sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 20 }} />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#333' }}>{r.does}</Typography>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.5 }}>
                    Assigned: {r.assign}
                  </Typography>
                </Box>
              ))}
            </Paper>
          ))}
        </Box>
      )}

      {tab !== 'roles' && (
      <Button
        variant="contained" size="large" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
        onClick={handleSave} disabled={saving}
        sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}
      >
        {saving ? 'Saving…' : 'Save Branding'}
      </Button>
      )}
    </Box>
    </Layout>
  );
}
